import Config from 'config';
import { useUnit } from 'effector-react';
import $$api from 'models/api';
import $$auth from 'models/auth';
import $$weakPassword from 'models/change-weak-password';
import { $global } from 'models/store-global';
import { useEffect } from 'react';
import { Alert } from 'react-bootstrap';
import { Navigate, Route, Routes } from 'react-router-dom';
import styled from 'styled-components';

import GuestApp from './GuestApp';
import UserApp from './UserApp';

import AccountPage from 'components/Account/AccountPage';
import { Container as AlertsContainer } from 'components/Alerts/Alerts';
import TempUserApp from 'components/Apps/TempUserApp';
import ProfileLinker from 'components/ProfileLinker/ProfileLinker';
import Header from 'components/StyledTemplates/Header';
import { Frame } from 'components/StyledTemplates/StyledTemplates';
import LoadingPage from 'components/pages/LoadingPage/LoadingPage';

import { lazyWithRetry } from 'utils/lazy-with-retry';

import useAdvisorLinker from 'hooks/useAdvisorLinker';
import useAdvisorsGroupLinker from 'hooks/useAdvisorsGroupLinker';
import useAutomaticAdvisorLinker from 'hooks/useAutomaticAdvisorLinker';
import useCookieChange from 'hooks/useCookieChange';
import { useCookiesAllowed } from 'hooks/useCookiesAllowed';
import useLogoutOnRefreshTokenCloseToExpire from 'hooks/useLogoutOnRefreshTokenCloseToExpire';
import usePromocode from 'hooks/usePromocode';
import useResources from 'hooks/useResources';
import { useSingleTab } from 'hooks/useSingleTab';
import useUrlTracking from 'hooks/useUrlTracking';
import useZendeskHelpHider from 'hooks/useZendeskHelpHider';

import { TRole } from 'types/Store';

const ClearCookiesPage = lazyWithRetry(() => import('components/pages/ClearCookiesPage/ClearCookiesPage'));
const ScriptsCheck = lazyWithRetry(() => import('components/pages/ScriptsCheck/ScriptsCheck'));

const { isMarketing } = Config;

const RoleAppMapping: Record<TRole, JSX.Element> = {
    user: <UserApp />,
    tempUser: <TempUserApp />,
    admin: <UserApp />,
    advisor: <UserApp />,
    guest: <GuestApp />,
};

const AppForRole = (props: { role: TRole }) => {
    return <>{RoleAppMapping[props.role] ?? GuestApp}</>;
};

const RouterApp = () => {
    useUrlTracking();
    useZendeskHelpHider();
    useLogoutOnRefreshTokenCloseToExpire();
    useAdvisorLinker();
    useAdvisorsGroupLinker();
    useAutomaticAdvisorLinker();
    usePromocode();
    useCookieChange();

    const { t } = useResources();
    const initialized = useUnit($$auth.$initialized);
    const isLeader = useSingleTab();
    const areCookiesAllowed = useCookiesAllowed();

    const userRole = useUnit($global.map((state) => state.auth?.userRole || 'guest'));
    const isUserRelevantSigned = useUnit($global.map((state) => state?.tac?.user?.isRelevantSigned || false));
    const isAdvisorRelevantSigned = useUnit($global.map((state) => state?.tac?.advisor?.isRelevantSigned || false));
    const isPasswordChangeRequired = useUnit($$weakPassword.$changeRequired);

    useEffect(() => {
        if (userRole !== 'guest' && (isUserRelevantSigned || isAdvisorRelevantSigned)) {
            $$api.misc.getPlansFx();
        }
    }, [userRole, isUserRelevantSigned, isAdvisorRelevantSigned]);

    if (!areCookiesAllowed) {
        return (
            <div className="d-flex flex-row justify-content-center">
                <AlertsContainer>
                    <Alert variant="danger">{t('common.cookies')}</Alert>
                </AlertsContainer>
            </div>
        );
    }

    if (!isLeader) {
        return (
            <div className="d-flex flex-row justify-content-center">
                <AlertsContainer>
                    <Alert variant="info">{t('common.onlyOneTab')}</Alert>
                </AlertsContainer>
            </div>
        );
    }

    if (!initialized) {
        return (
            <>
                <Header />
                <LoadingPage />
            </>
        );
    }

    if (isMarketing) {
        return <Header />;
    }

    if (isPasswordChangeRequired) {
        return (
            <>
                <Header />
                <Routes>
                    <Route path="/reset-password" element={<AccountPage />} />
                    <Route path="/*" element={<Navigate to="/reset-password" />} />
                </Routes>
            </>
        );
    }

    return (
        <>
            <ProfileLinker />

            <Wrapper>
                <Header />
                <Frame className="body-wrapper">
                    <Routes>
                        <Route path="/clear-cookies" element={<ClearCookiesPage />} />
                        <Route path="/scripts" element={<ScriptsCheck />} />
                        <Route element={<AppForRole role={userRole} />} />
                        <Route path="/*" element={<AppForRole role={userRole} />} />
                    </Routes>
                </Frame>
            </Wrapper>
        </>
    );
};

const Wrapper = styled(Frame)`
    min-height: 100vh;

    .body-wrapper {
        width: 100%;
        flex: 1;
        height: inherit;
        box-sizing: border-box;

        @media (max-width: 991px) {
            justify-content: flex-start;
        }
    }
`;

export default RouterApp;
